import { apiRoot, isProduction, localDev, serverCodes, fullAppSuiteRootURL, isOnPrem, noAPI } from "../../data/constants.ts";
import { internalData } from "../../store/store.ts";
import { devlog } from "../../util/utils.ts";
import { asApp } from '../../util/singletons.ts';
import { sendEv } from '../../data/events.ts';

let _getUserDataInFlight: Promise<AuthResponse> | null = null;

// Always include credentials so session cookies are sent cross-origin (e.g. localhost → staging)
// On-prem has no server API. A call gets a plain failure reply and never
// leaves the page.
export const apiFetch = async (url: string, init?: RequestInit): Promise<Response> => {
  if (isOnPrem) {
    console.warn(`[API] ${url} skipped: no server API on-prem`);
    return new Response(JSON.stringify({ success: false, errorMessage: 'No server API on-prem' }),
      { status: 200, headers: { 'Content-Type': 'application/json' } });
  }
  return fetch(url, { credentials: 'include', ...init });
};

/**
 * Fetches a static JSON resource (config, settings, lang)
 * @param path - Path relative to app suite root or absolute URL
 * @returns Promise<T | null> - The parsed JSON data, or null if 404 or other error
 */
export const fetchResource = async <T = any>(path: string): Promise<T | null> => {
  const url = path.startsWith('http') || path.startsWith('/') || path.startsWith('./')
    ? path 
    : `${fullAppSuiteRootURL}/${path}`;
    
  try {
    const response = await fetch(url);
    if (response.status === 404) return null;
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (err) {
    return null;
  }
};

/**
 * The backend is PHP. An uncaught notice/warning/fatal error prints as plain
 * text or HTML ahead of (or instead of) the real JSON payload, and proxies/load
 * balancers return their own HTML error pages too. Either way the body is not
 * JSON we should try to parse, and it must never reach the user verbatim.
 */
const looksLikePhpOrHtmlError = (text: string): boolean => {
  const head = text.slice(0, 500);
  return /^\s*</.test(head)
    || /\b(Fatal error|Parse error|Warning|Deprecated|Notice)\b:/i.test(head)
    || /Stack trace:/i.test(head);
};

/**
 * Logs and surfaces a malformed/unexpected server response. Always logs, both
 * to the console and to the server-side event log (via as-log-error, so it
 * goes through the same pipeline as any other logged error). Critical failures
 * are ones the app can't meaningfully continue past (e.g. the auth bootstrap
 * check) and open the blocking error modal; everything else just shows a
 * dismissible as-notify toast so the user knows to retry.
 */
const reportBadResponse = (context: string, critical: boolean, detail: string): void => {
  console.error(`[API] ${context}:`, detail);
  sendEv(window, 'as-log-error', {
    detail: {
      origin: `api:${context}`,
      error: detail,
      importance: critical ? 'critical' : 'regular',
      notify: critical ? undefined : 'Something went wrong talking to the server. Please try again.',
      notifyDictKey: critical ? undefined : 'notifications.api.badResponse',
    }
  });
  if (critical) {
    sendEv(asApp(), 'error', {
      bubbles: false,
      detail: { error: new Error(detail), origin: `api:${context}` }
    });
  }
};

/**
 * Validates server response by checking status code and parsing JSON.
 * @param response - The fetch Response object
 * @param context - Short label identifying the call site, used in logs and the error modal (e.g. 'getUserData')
 * @param critical - If true, a bad response opens the blocking error modal instead of an as-notify toast
 * @returns Promise<any> - The parsed JSON response
 * @throws Error - If status is not 200, response is empty, or JSON parsing fails. The thrown message is
 *   always a safe, generic string — raw PHP/HTML output is logged but never put in the thrown error.
 */
const validatedServerResponse = async (response: Response, context: string, critical = false): Promise<any> => {
  // Check if response status is 200
  if (response.status !== 200) {
    const detail = `Server returned status ${response.status}: ${response.statusText}`;
    reportBadResponse(context, critical, detail);
    throw new Error(detail);
  }

  // Check if response has content
  const responseText = await response.text();
  devlog("[API]", responseText)
  if (!responseText || responseText.trim() === '') {
    reportBadResponse(context, critical, 'Server returned empty response');
    throw new Error('Server returned empty response');
  }

  if (looksLikePhpOrHtmlError(responseText)) {
    reportBadResponse(context, critical, `Server returned non-JSON output: ${responseText.slice(0, 500)}`);
    throw new Error('Server returned an unexpected response.');
  }

  // Parse JSON
  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    reportBadResponse(context, critical, `Failed to parse server response as JSON: ${responseText.slice(0, 200)}`);
    throw new Error('Server returned an unexpected response.');
  }
};

/** What every account and organisation call resolves to. */
export interface ApiResult<T = any> {
  success: boolean;
  data?: T;
  errorCode?: number;
  errorMessage?: string;
}

/**
 * Calls one `?function=` endpoint and normalises the reply. Every account and
 * organisation call below is this same shape: post a form (or GET), then turn
 * both a `success: false` reply and a thrown transport error into the same
 * result object, so callers only have to check `success`.
 *
 * `context` is the label used in logs when it differs from the endpoint name.
 * `dev` routes to the local `/api` proxy, which the organisation calls use
 * when running against a dev backend.
 */
const callApi = async <T = any>({
  fn, context = fn, fields, method = 'POST', dev = false, failMessage,
}: {
  fn: string;
  context?: string;
  fields?: Record<string, string>;
  method?: 'GET' | 'POST';
  dev?: boolean;
  failMessage: string;
}): Promise<ApiResult<T>> => {
  const root = dev ? '/api' : apiRoot;
  try {
    const init: RequestInit = { method };
    if (fields) {
      const body = new FormData();
      for (const [key, value] of Object.entries(fields)) body.append(key, value);
      init.body = body;
    }
    const data = await validatedServerResponse(await apiFetch(`${root}?function=${fn}`, init), context);
    if (!data.success) {
      return {
        success: false,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage || data.message || failMessage,
      };
    }
    return data;
  } catch (error) {
    console.error(`${context} error:`, error.message);
    return { success: false, errorMessage: error.message || failMessage };
  }
};


export interface UserData {
  UUID: string;
  firstName: string;
  lastName: string;
  eMail: string;
  license: {
    startDate: string | null;
    endDate: string | null;
    type: string | null;
    /** Number of licensed seats; 0 = no restriction. The API sends this as a numeric string. */
    seats?: number | string;
    applications?: string[];
  };
  organization: string;
  /** @deprecated use environment instead */
  organizationId?: number;
  environment?: string;
  /**
   * When the user accepted the current terms and conditions. `null` means the
   * terms have changed since they last accepted (or they never did) and the app
   * must get a fresh acceptance before it lets them continue - see
   * as-modal-accept-terms. Absent entirely on older backends and dummy data,
   * which means "nothing to accept".
   */
  termsAccepted?: string | null;
}

export interface AuthResponse {
  isAuthenticated: boolean;
  userData: UserData | null;
  isDummyData?: boolean;
  message?: string;
  success?: boolean;
  errorCode?: string | number;
  /**
   * True when the auth check itself failed (network error, 500, garbage
   * response) — we never heard a real answer, so `isAuthenticated: false`
   * does NOT mean "signed out". Callers must not redirect to sign-in on it.
   */
  transportFailed?: boolean;
}

/**
 * Signs in a user with the provided form data, then gets user data if successful.
 * @param formData - Form data containing email and password
 * @returns Promise with the authentication response
 */
export const signIn = async (formData: FormData): Promise<AuthResponse> => {
  internalData.pendingAuth = true;

  sendEv(window, 'as-auth-sign-in-started', { detail: { formData } });

  try {
    const response = await apiFetch(`${apiRoot}?function=login`, {
      method: 'POST',
      body: formData,
    });

    const data = await validatedServerResponse(response, 'signIn');

    // Check if the response indicates success
    if (!data.success) {
      return {
        isAuthenticated: false,
        userData: null,
        message: data.message || data.errorMessage || 'Sign in failed',
        success: false,
        errorCode: data.errorCode
      };
    }

    // If authentication is successful, get user data and update store
    const userDataResponse = await getUserData();
    return userDataResponse;
  } catch (error) {
    console.error('signIn error:', error.message);
    return {
      isAuthenticated: false,
      userData: null,
      message: error.message || 'Network error occurred',
      success: false,
      errorCode: '0' // UNKNOWN_ERROR
    };
  } finally {
    internalData.pendingAuth = false;
  }
};

export type DefaultLogEventStrings =
  'sign_in'
  | 'sign_out'
  | 'app_opened'
  | 'new_session'
  | 'import_mesh'
  | 'export_mesh'
  | 'restore_session_zip'
  | 'save_session_zip'
  | 'transfer_mesh'
  | 'auto_restore_session' // later?

export const sendLogToServer = async (event: string, data?: any, subEvent?: string) => {
  const appKey = internalData.appId;
  //TODO: test
  if (localDev) return
  if (appKey === undefined) {
    console.error('appKey is undefined');
    return Promise.reject(new Error('appKey is undefined'));
  }
  const formData = new FormData();
  formData.append('event', event);
  formData.append('application', appKey);
  if (subEvent) {
    formData.append('subEvent', subEvent);
  }
  if (data) {
    formData.append('dataString', JSON.stringify(data));
  }
  devlog('sendLogEvent', event, appKey);
  const response = await apiFetch(`${apiRoot}?function=logEvent`, {
    method: 'POST',
    body: formData
  })
  // A failed log call is reported to the console only. requestSucceeded()
  // would raise as-log-error, which logs to the server, which fails again,
  // without end.
  const resText = await response.text();
  if (!response.ok || looksLikePhpOrHtmlError(resText)) {
    console.warn(`[API] sendLogToServer: server returned ${response.status}`, resText.slice(0, 200));
    return;
  }
  try {
    return JSON.parse(resText);
  } catch {
    console.warn('[API] sendLogToServer: response not valid JSON', resText.slice(0, 200));
  }
}

/**
 * Sends a log event to the server using navigator.sendBeacon (ideal for unload events)
 * @param event - Event name string
 * @param data - Optional data object to log
 */
export const sendBeaconToServer = (event: string, data?: any): boolean => {
  const appKey = internalData.appId;
  if (localDev || !appKey) return false;

  const formData = new FormData();
  formData.append('event', event);
  formData.append('application', appKey);
  if (data) {
    formData.append('dataString', JSON.stringify(data));
  }
  return navigator.sendBeacon(`${apiRoot}?function=logEvent`, formData);
};

/**
 * The one place auth state is written. Updates the store and, unless told not
 * to, announces it on `as-auth-update` for anyone not reading the store.
 */
const setAuthState = (isAuthenticated: boolean, userData: UserData | null, notify = true): void => {
  internalData.userData = userData;
  internalData.isAuthenticated = isAuthenticated;
  if (notify) sendEv(window, 'as-auth-update', { detail: { isAuthenticated, userData } });
};

const dummyUserData: UserData = {
  UUID: "localdev",
  firstName: "Local",
  lastName: "Development",
  eMail: "anon@trinckle.com",
  license: { startDate: "0", endDate: "999999999", type: "Free" },
  organization: "Trinckle Dev",
};

const _getUserDataImpl = async (): Promise<AuthResponse> => {
  // onprem: no sign in. The user comes from data/user.cfg.json at the suite
  // root, a static file, or is the placeholder.
  if (isOnPrem) {
    let userData = await fetchResource<UserData>('data/user.cfg.json');
    if (!userData) {
      userData = await fetchResource<UserData>('data/userData.json');
      if (userData) console.warn('[DEPRECATED] data/userData.json: rename it to data/user.cfg.json.');
    }
    const u = userData ?? dummyUserData;
    // Always notify: the suite treats an on-prem session as signed in.
    setAuthState(true, u, true);
    return { isAuthenticated: true, isDummyData: true, userData: u, success: true };
  }

  // localDev: skip the network fetch — the file rarely exists in local setups and
  // the 404 shows in the browser console even when JS handles it gracefully
  // noAPI: sandboxed contexts (e.g. as-code-demo's srcdoc iframe) have no
  // reachable API at all — same dummy-data path, otherwise this is a critical
  // call and a 404 pops the blocking error modal.
  if (localDev || noAPI) {
    setAuthState(true, dummyUserData, false);
    return { isAuthenticated: true, isDummyData: true, userData: dummyUserData, success: true };
  }

  try {
    const response = await apiFetch(`${apiRoot}?function=getUserData`);
    // Not critical: a transport failure here degrades to an
    // unauthenticated-but-usable app (see transportFailed below). Leaving it
    // critical popped the blocking error modal *and* navigated away, so one
    // 500 here took the whole app out.
    const data = await validatedServerResponse(response, 'getUserData', false);

    if (!data.success) {
      setAuthState(false, null);

      return {
        isAuthenticated: false,
        userData: null,
        success: false,
        message: data.message || data.errorMessage || 'Failed to get user data'
      };
    }

    setAuthState(true, data.data);

    return {
      isAuthenticated: true,
      userData: data.data,
      success: true
    };
  } catch (error) {
    console.error('getUserData error:', error.message);

    setAuthState(false, null);

    return {
      isAuthenticated: false,
      userData: null,
      success: false,
      // We never heard back properly, so we do not actually know whether the
      // user is signed in. Callers must not treat this as "signed out".
      transportFailed: true,
      message: error.message || 'Failed to get user data'
    };
  }
};

export const getUserData = (): Promise<AuthResponse> => {
  if (!_getUserDataInFlight) {
    _getUserDataInFlight = _getUserDataImpl().finally(() => { _getUserDataInFlight = null; });
  }
  return _getUserDataInFlight;
};

export const getDummyUserData = async (succeed = true): Promise<AuthResponse> => {
  return new Promise<AuthResponse>((resolve) => {
    setTimeout(() => {
      const userData: UserData = {
        UUID: 'dev-user-uuid-123',
        firstName: 'Dev',
        lastName: 'User',
        eMail: 'dev@trinckle.com',
        license: {
          type: 'free',
          startDate: '2025-02-12 00:00:00',
          endDate: '2025-07-15 00:00:00',
          applications: [
            'roboticFingers',
            'workpieceCreator',
            'clamping'
          ]
        },
        organization: 'Trinckle GmbH'
      }

      setAuthState(succeed, succeed ? userData : null);

      resolve({
        isAuthenticated: succeed,
        userData: succeed ? userData : null,
        success: succeed
      });
    }, 500);
  });
};


/**
 * Signs the user out. By default the page reloads afterwards, which lands the
 * user on the sign-in redirect. Pass `{ reload: false }` when the caller wants
 * to navigate somewhere else itself (the accept-terms Cancel goes to the
 * storefront). Resolves true when the server confirmed the sign-out.
 */
export const logOut = async ({ reload = true }: { reload?: boolean } = {}): Promise<boolean> => {
  try {
    const response = await apiFetch(`${apiRoot}?function=logout`, {
      method: 'POST',
    });

    const data = await validatedServerResponse(response, 'logOut');

    if (!data.success) {
      throw new Error(data.message || data.errorMessage || 'Logout failed');
    }

    setAuthState(false, null, false);
    if (reload) window.location.replace(window.location.pathname + window.location.hash);
    return true;
  } catch (error) {
    console.error('logout failed:', error.message);
    sendEv(window, 'as-notify', {
      detail: { defaultText: 'Sign out failed. Please try again.', dictKey: 'notifications.auth.signOut.error', type: 'error' },
    });
    return false;
  }
};

/**
 * Records that the signed-in user accepted the current terms and conditions.
 * On success the user data is fetched again so the store (and anyone on
 * 'as-auth-update') sees the new `termsAccepted` value. Resolves true on
 * success; a failure shows an as-notify toast and resolves false so the
 * accept-terms modal can stay open.
 */
export const acceptTerms = async (): Promise<boolean> => {
  try {
    const response = await apiFetch(`${apiRoot}?function=acceptTerms`, {
      method: 'POST',
    });

    const data = await validatedServerResponse(response, 'acceptTerms');

    if (!data.success) {
      throw new Error(data.message || data.errorMessage || 'Accepting the terms failed');
    }

    await getUserData();
    return true;
  } catch (error) {
    console.error('acceptTerms failed:', error.message);
    sendEv(window, 'as-notify', {
      detail: { defaultText: 'Could not save your acceptance. Please try again.', dictKey: 'notifications.auth.terms.error', type: 'error' },
    });
    return false;
  }
};



/**
 * Utility function to check if server response is OK
 * @param response - Server response object
 * @returns boolean indicating if response was successful
 */
export const serverOK = (response: any): boolean => {
  return response.success;
};
export const requestSucceeded = async (response: Response, context: string = 'requestSucceeded'): Promise<any> => {
  devlog(response)
  const resText = await response.text();

  if (looksLikePhpOrHtmlError(resText)) {
    reportBadResponse(context, false, `Server returned non-JSON output: ${resText.slice(0, 500)}`);
    throw new Error('Server returned an unexpected response.');
  }

  let resData: any;
  try {
    resData = JSON.parse(resText);
  } catch (err) {
    reportBadResponse(context, false, `Response not valid JSON: ${resText.slice(0, 200)}`);
    throw new Error('Server returned an unexpected response.');
  }

  if (resData.success) return resData;
  throw { errorCode: resData.errorCode, errorMessage: resData.errorMessage };
}

/**
 * Changes the signed-in user's password.
 */
export const changePassword = (
  oldPassword: string,
  newPassword: string,
  confirmPassword: string,
): Promise<ApiResult> => callApi({
  fn: 'changePassword',
  fields: { oldPassword, newPassword, confirmPassword },
  failMessage: 'Password change failed',
});

/** Requests a password recovery code by email. */
export const lostPassword = (email: string): Promise<ApiResult> => callApi({
  fn: 'lostPassword',
  fields: { eMail: email },
  failMessage: 'Password recovery request failed',
});

/** Checks that a password recovery code is still valid. */
export const checkRegainCode = (code: string): Promise<ApiResult> => callApi({
  fn: 'checkRegainCode',
  fields: { code },
  failMessage: 'Recovery code validation failed',
});

/**
 * Resets the password behind a recovery code. The server signs the user in and
 * sets the session cookie, so the user data is fetched again on success.
 */
export const regainAccess = async (
  code: string,
  newPassword: string,
  confirmPassword: string,
): Promise<ApiResult> => {
  const result = await callApi({
    fn: 'regainAccess',
    fields: { code, newPassword, confirmPassword },
    failMessage: 'Account recovery failed',
  });
  if (result.success) await getUserData();
  return result;
};

/** Registers a new user. The account is not usable until it is activated. */
export const register = (
  email: string,
  firstName: string,
  lastName: string,
  organizationName: string,
  captcha: string,
  marketingOptIn: boolean,
  marketingSource?: string,
): Promise<ApiResult> => callApi({
  fn: 'register',
  fields: {
    eMail: email,
    firstName,
    lastName,
    organizationName,
    captcha,
    marketingOptIn: marketingOptIn ? '1' : '0',
    ...(marketingSource ? { marketingSource } : {}),
  },
  failMessage: 'Registration failed',
});

/** Checks that an account activation code is still valid. */
export const checkActivationCode = (code: string): Promise<ApiResult> => callApi({
  fn: 'checkActivationCode',
  fields: { code },
  failMessage: 'Activation code validation failed',
});

/**
 * Activates a new account and sets its first password. The server signs the
 * user in and sets the session cookie, so the user data is fetched again on
 * success.
 */
export const activateUser = async (
  code: string,
  password: string,
  confirmPassword: string,
): Promise<ApiResult> => {
  const result = await callApi({
    fn: 'activateUser',
    fields: { code, password, confirmPassword },
    failMessage: 'Account activation failed',
  });
  if (result.success) await getUserData();
  return result;
};

/** The signed-in user's license data. */
export const getLicenseData = (): Promise<ApiResult> => callApi({
  fn: 'getLicenseData',
  method: 'GET',
  failMessage: 'Failed to get license data',
});

/** One member of the signed-in user's organisation. */
export interface OrgUser {
  UUID: string;
  firstName: string;
  lastName: string;
  eMail: string;
  /** A = active, I = inactive. */
  status: 'A' | 'I';
}

/** Everyone in the signed-in user's organisation. */
export const getOrgUserList = (dev = false): Promise<ApiResult<OrgUser[]>> => callApi<OrgUser[]>({
  fn: 'listUsersOfCompany',
  context: 'getOrgUserList',
  method: 'GET',
  dev,
  failMessage: 'Failed to get the organisation user list',
});

/** Invites someone into the signed-in user's organisation. */
export const sendOrgInvite = (
  email: string,
  firstName: string,
  lastName: string,
  dev = false,
): Promise<ApiResult> => callApi({
  fn: 'invite',
  context: 'sendOrgInvite',
  fields: { eMail: email, firstName, lastName },
  dev,
  failMessage: 'Sending the invite failed',
});

/** Sends the activation code again, for a registration that never completed. */
export const resendActivationCode = (email: string, dev = false): Promise<ApiResult> => callApi({
  fn: 'resendActivation',
  context: 'resendActivationCode',
  fields: { eMail: email },
  dev,
  failMessage: 'Resending the activation code failed',
});

/** Sends an organisation invite again. */
export const resendOrgInvite = (email: string, dev = false): Promise<ApiResult> => callApi({
  fn: 'resendInvitation',
  context: 'resendOrgInvite',
  fields: { eMail: email },
  dev,
  failMessage: 'Resending the invite failed',
});

/**
 * Everything in this module as one object, for consumers that take the whole
 * API surface in a single import (as-home does). The named exports above are
 * the same functions.
 */
export const AsApi = {
  apiRoot,
  serverCodes,
  fetchResource,

  // Auth
  signIn,
  logOut,
  getUserData,
  getDummyUserData,
  acceptTerms,

  // Account
  changePassword,
  lostPassword,
  checkRegainCode,
  regainAccess,
  register,
  checkActivationCode,
  activateUser,
  getLicenseData,

  // Organisation
  getOrgUserList,
  sendOrgInvite,
  resendOrgInvite,
  resendActivationCode,

  // Logging
  sendLogToServer,
  sendBeaconToServer,

  serverOK,
};
